import { FormikProvider, useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { Loader } from 'app/shared/loader/loader';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import React from 'react';
import {
  fetchBuildingPurposeByTypeQuery,
  fetchBuildingTypeNationalClassificationForCBPTypeQuery,
  fetchConstructionBuildingProcedureByTypeCodebookQuery,
  fetchConstructionCommencementQuery,
  fetchGreenInfrastructureTypeQuery,
  fetchRequirementGroupQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { queryClient } from 'app/app';
import {
  initialIntervention,
  Intervention,
} from 'app/edozvola/cases/construction-building-procedure/interventions/model/intervention.model';
import { interventionValidationSchema } from './intervention.schema';
import {
  fetchInterventionQuery,
  saveInterventionQuery,
} from 'app/edozvola/cases/construction-building-procedure/service/construction-building-procedure.queries';
import { NumericFormat } from 'react-number-format';
import { ConstructionBuildingProcedureType } from 'app/edozvola/codebooks/model/construction-building-procedure-codebook.model';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";
import {CASE_TAB_ENUM} from "app/config/constants";
import { revalidateTabsThunk } from 'app/shared/reducers/validationerrors/validation-errors.reducer';
import getStore from 'app/config/store';

export interface IInterventionFormProps {
  idIntervention: string | null;
  setIdIntervention: any;
  readOnly: boolean;
  variant: string;
}

const InterventionForm = (props: IInterventionFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const {
    data: interventionData,
    isLoading: interventionDataLoading
  } = fetchInterventionQuery(props.idIntervention, props.variant);

  const { data: constructionBuildingProcedureOptions, isLoading: constructionBuildingProcedureOptionsLoading } =
    fetchConstructionBuildingProcedureByTypeCodebookQuery(uuid, ConstructionBuildingProcedureType.ZAHVAT);

  const { data: buildingPurposeOptions, isLoading: buildingPurposeOptionsLoading } = fetchBuildingPurposeByTypeQuery(ConstructionBuildingProcedureType.ZAHVAT);
  const { data: requirementGroupOptions, isLoading: requirementGroupOptionsLoading } = fetchRequirementGroupQuery();
  const {
    data: constructionCommencementOptions,
    isLoading: constructionCommencementOptionsLoading
  } = fetchConstructionCommencementQuery();
  const {
    data: buildingTypeNationalClassificationOptions,
    isLoading: buildingTypeNationalClassificationOptionsLoading
  } =
    fetchBuildingTypeNationalClassificationForCBPTypeQuery(ConstructionBuildingProcedureType.ZAHVAT);
  const { data: greenInfrastructureTypeOptions, isLoading: greenInfrastructureTypeOptionsLoading } = fetchGreenInfrastructureTypeQuery();

  const dispatch = getStore().dispatch;

  const formik = useFormik({
    initialValues: interventionData || initialIntervention,
    validationSchema: interventionValidationSchema,
    onSubmit: (values: Intervention) => {
      saveInterventionMutation(values);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const {
    mutate: saveInterventionMutation,
    isPending: isInterventionSaving
  } = saveInterventionQuery(uuid, props.variant, () => {
    closeModal();
  });

  const closeModal = () => {
    props.setIdIntervention(null);
    queryClient.invalidateQueries({ queryKey: ['intervention'] });
    dispatch(revalidateTabsThunk());
  };

  const openIspuLocator = (interventionUuid: string) => {
    const url = window.location.origin + `/ispu-locator/interventions/${interventionUuid}&${CASE_TAB_ENUM.CONSTRUCTION_BUILDING_PROCEDURE}`;

    window.open(url, '_blank');
  };

  return (
    <>
      {interventionDataLoading && <Loader />}
      {!interventionDataLoading && (
        <Modal scrollable isOpen={props.idIntervention !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled zahvata</ModalHeader>
          ) : (
            <ModalHeader>{props.idIntervention === '' ? 'Unos zahvata' : 'Uređivanje zahvata'}</ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta zahvata'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu zahvata'}
                      onItemSelect={selected => formik.setFieldValue('constructionBuildingProcedure', selected)}
                      selected={formik.values.constructionBuildingProcedure}
                      labelKey={'value'}
                      options={constructionBuildingProcedureOptionsLoading ? [] : constructionBuildingProcedureOptions}
                      disabled={props.readOnly}
                      error={formik.errors.constructionBuildingProcedure as string}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Namjena'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi namjenu'}
                      onItemSelect={selected => formik.setFieldValue('buildingPurpose', selected)}
                      selected={formik.values.buildingPurpose}
                      labelKey={'value'}
                      options={buildingPurposeOptionsLoading ? [] : buildingPurposeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.buildingPurpose as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Dodatni opis namjene'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.buildingPurposeAdditionalDescription}
                      error={
                        formik.errors.buildingPurposeAdditionalDescription ? `${formik.errors.buildingPurposeAdditionalDescription}` : ''
                      }
                      onChange={event => formik.setFieldValue('buildingPurposeAdditionalDescription', event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Skupina prema zahtjevnosti'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi skupinu prema zahtjevnosti'}
                      onItemSelect={selected => formik.setFieldValue('requirementGroup', selected)}
                      selected={formik.values.requirementGroup}
                      labelKey={'value'}
                      options={requirementGroupOptionsLoading ? [] : requirementGroupOptions}
                      disabled={props.readOnly}
                      error={formik.errors.requirementGroup as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Zahvat u prostoru (*.gml)'} />
                    <div className="d-flex ispu-input-wrapper">
                      <Input
                        disabled={true}
                        placeholder={formik.values?.geometryExists ? 'Spremljena je GML datoteka.' : 'Ne postoji spremljena GML datoteka.'}
                        error={
                          !props.idIntervention
                            ? 'Potrebno je prvo spremiti zahvat prije otvaranja ISPU lokatora.'
                            : !formik.values?.geometryExists
                              ? '  '
                              : ''
                        }
                      />
                      <Button
                        disabled={!props.idIntervention}
                        label="ISPU lokator"
                        onClick={() => openIspuLocator(formik.values?.uuid)}
                        color="info"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Započetost izgradnje'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi započetost izgradnje'}
                      onItemSelect={selected => formik.setFieldValue('constructionCommencement', selected)}
                      selected={formik.values.constructionCommencement}
                      labelKey={'value'}
                      options={constructionCommencementOptionsLoading ? [] : constructionCommencementOptions}
                      disabled={props.readOnly}
                      error={formik.errors.constructionCommencement as string}
                      showErrorMessage
                      positionFixed
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'NKVG'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi NKVG'}
                      onItemSelect={selected => formik.setFieldValue('buildingTypeNationalClassification', selected)}
                      selected={formik.values.buildingTypeNationalClassification}
                      labelKey={'value'}
                      options={buildingTypeNationalClassificationOptionsLoading ? [] : buildingTypeNationalClassificationOptions}
                      disabled={props.readOnly}
                      error={formik.errors.buildingTypeNationalClassification as string}
                      positionFixed
                    />
                  </Col>
                </Row>
                <hr />
                <div className={'form-header'}>Zelena infrastruktura</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu'}
                      onItemSelect={selected => formik.setFieldValue('greenInfrastructureType', selected)}
                      selected={formik.values.greenInfrastructureType}
                      labelKey={'value'}
                      options={greenInfrastructureTypeOptionsLoading ? [] : greenInfrastructureTypeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.greenInfrastructureType as string}
                      showErrorMessage
                      positionFixed
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Površina'} />
                    <div className="d-flex align-items-center border rounded px-2">
                      <NumericFormat
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        value={formik.values.greenInfrastructureArea || ''}
                        customInput={Input}
                        onValueChange={values => {
                          const { value } = values;
                          formik.setFieldValue(`greenInfrastructureArea`, value);
                        }}
                        disabled={props.readOnly}
                        className="flex-grow-1 border-0 shadow-none"
                      />
                      <span className="ms-2 text-muted">m²</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={isInterventionSaving}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default InterventionForm;
